import { v4 as uuidv4 } from 'uuid'
import { normalizeInstrument } from './synthInstruments.js'

export const SYNTH_MAX_TRACKS = 8
export const SYNTH_LAYER_COUNT = 2

export const SYNTH_WAVEFORMS = ['sine', 'square', 'triangle', 'saw', 'noise']

export const SYNTH_FORMAT_VERSION = 2

export const SYNTH_DEFAULT_TRACK_NAME = (index) => `Track ${index + 1}`
export const SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS = 160
export const SYNTH_DEFAULT_NOTE = 60
export const SYNTH_DEFAULT_INSTRUMENT = 'synth'
export const SYNTH_DEFAULT_TRACK_VOLUME = 1
export const SYNTH_DEFAULT_REST_DURATION_TICKS = 100
export const SYNTH_DEFAULT_TRACK_ADSR = Object.freeze({
  attack: 0.002,
  decay: 0.07,
  sustain: 0.8,
  release: 0.05,
})
export const SYNTH_DEFAULT_TRACK_VIBRATO = Object.freeze({
  enabled: false,
  rateHz: 0,
  depthHz: 0,
})
export const SYNTH_DEFAULT_TEMPO = Object.freeze({
  bpm: 120,
  timeSignature: [4, 4],
  grid: '1/16',
})
export const SYNTH_DEFAULT_LAYER = Object.freeze({
  enabled: true,
  waveform: 'sine',
  pulseWidthPercent: 50,
  volumePercent: 100,
  detuneCents: 0,
})

export function createDefaultSynthResource() {
  return {
    uuid: uuidv4(),
    name: 'New Synth',
    filename: '',
    formatVersion: SYNTH_FORMAT_VERSION,
    tempo: createDefaultTempo(),
    tracks: Array.from({ length: SYNTH_MAX_TRACKS }, (_, index) => createDefaultTrack(index)),
  }
}

export function createDefaultTempo() {
  return { ...SYNTH_DEFAULT_TEMPO, timeSignature: [...SYNTH_DEFAULT_TEMPO.timeSignature] }
}

export function createDefaultTrack(index = 0) {
  return {
    enabled: true,
    name: SYNTH_DEFAULT_TRACK_NAME(index),
    instrument: SYNTH_DEFAULT_INSTRUMENT,
    volume: SYNTH_DEFAULT_TRACK_VOLUME,
    adsr: { ...SYNTH_DEFAULT_TRACK_ADSR },
    vibrato: { ...SYNTH_DEFAULT_TRACK_VIBRATO },
    layers: Array.from({ length: SYNTH_LAYER_COUNT }, () => createDefaultLayer()),
    sequence: [],
  }
}

export function createDefaultLayer() {
  return { ...SYNTH_DEFAULT_LAYER }
}

export function normalizeSynthResource(input = {}) {
  const resource = {
    uuid: input.uuid ?? uuidv4(),
    name: (input.name ?? 'New Synth').toString(),
    filename: (input.filename ?? '').toString(),
    formatVersion: SYNTH_FORMAT_VERSION,
    tempo: normalizeTempo(input?.tempo),
    tracks: [],
  }

  const tracks = Array.isArray(input.tracks) ? input.tracks : []
  for (let i = 0; i < SYNTH_MAX_TRACKS; i++) {
    resource.tracks.push(normalizeSynthTrack(tracks[i], i))
  }
  return resource
}

export function normalizeTempo(input = {}) {
  const bpm = clampNumber(input?.bpm, 40, 240, SYNTH_DEFAULT_TEMPO.bpm)
  const timeSignature = Array.isArray(input?.timeSignature) && input.timeSignature.length >= 2
    ? [clampNumber(input.timeSignature[0], 1, 32, SYNTH_DEFAULT_TEMPO.timeSignature[0]), clampNumber(input.timeSignature[1], 1, 32, SYNTH_DEFAULT_TEMPO.timeSignature[1])]
    : [...SYNTH_DEFAULT_TEMPO.timeSignature]
  // Matches the note values getGridStepDenominator() already converts.
  const grid = ['1/1', '1/2', '1/4', '1/8', '1/16', '1/32'].includes(input?.grid) ? input.grid : SYNTH_DEFAULT_TEMPO.grid
  return { bpm, timeSignature, grid }
}

export function normalizeSynthTrack(input = {}, index = 0) {
  const track = {
    enabled: input?.enabled !== false,
    name: (input?.name ?? SYNTH_DEFAULT_TRACK_NAME(index)).toString(),
    instrument: normalizeInstrument(input?.instrument),
    volume: clampNumber(input?.volume, 0, 1, SYNTH_DEFAULT_TRACK_VOLUME),
    adsr: normalizeAdsr(input?.adsr),
    vibrato: normalizeVibrato(input?.vibrato),
    layers: [],
    sequence: [],
  }

  const layers = Array.isArray(input?.layers) ? input.layers : []
  for (const layer of layers) {
    track.layers.push(normalizeSynthLayer(layer))
  }

  track.sequence = normalizeSequenceTimeline(input?.sequence)
  return track
}

export function normalizeSynthLayer(input = {}) {
  const waveform = SYNTH_WAVEFORMS.includes(input?.waveform) ? input.waveform : SYNTH_DEFAULT_LAYER.waveform
  return {
    enabled: input?.enabled !== false,
    waveform,
    pulseWidthPercent: clampNumber(input?.pulseWidthPercent, 0, 100, SYNTH_DEFAULT_LAYER.pulseWidthPercent),
    volumePercent: clampNumber(input?.volumePercent, 0, 100, SYNTH_DEFAULT_LAYER.volumePercent),
    detuneCents: clampNumber(input?.detuneCents, -1200, 1200, SYNTH_DEFAULT_LAYER.detuneCents),
  }
}

export function normalizeSequenceEvent(input = {}) {
  // Legacy Designer resources called the raw .synth duration value `durationMs`.
  // The wire value is musical ticks (480 PPQ), so accept the old key only at this boundary.
  const durationTicks = clampNumber(input?.durationTicks ?? input?.durationMs, 1, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS)
  const note = input?.note == null ? null : clampInteger(input.note, 0, 127, null)
  const startTick = clampNumber(input?.startTick, 0, Number.POSITIVE_INFINITY, 0)
  return { note, startTick, durationTicks }
}

export function normalizeSequenceTimeline(sequence = []) {
  let legacyCursor = 0
  const events = []
  for (const input of Array.isArray(sequence) ? sequence : []) {
    const durationTicks = clampNumber(input?.durationTicks ?? input?.durationMs, 1, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS)
    const hasExplicitStart = Number.isFinite(Number(input?.startTick))
    const startTick = hasExplicitStart ? Math.max(0, Number(input.startTick)) : legacyCursor
    if (!hasExplicitStart) legacyCursor += durationTicks
    if (input?.note == null) continue
    const note = clampInteger(input.note, 0, 127, null)
    if (note != null) events.push({ note, startTick, durationTicks })
  }
  return sortSequenceEvents(events)
}

export function sortSequenceEvents(events) {
  return events.sort((a, b) => a.startTick - b.startTick || a.note - b.note || a.durationTicks - b.durationTicks)
}

export function midiToNoteName(midi) {
  const value = Number(midi)
  if (!Number.isInteger(value) || value < 0 || value > 127) return ''
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
  const note = names[value % 12]
  const octave = Math.floor(value / 12) - 1
  return `${note}${octave}`
}

export function noteNameToMidi(name) {
  const text = String(name ?? '').trim().toUpperCase()
  const match = /^([A-G])(#?)(-?\d+)$/.exec(text)
  if (!match) return null
  const pitch = `${match[1]}${match[2] || ''}`
  const octave = Number(match[3])
  const names = { C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11 }
  const midi = (octave + 1) * 12 + names[pitch]
  return Number.isInteger(midi) && midi >= 0 && midi <= 127 ? midi : null
}

export const SYNTH_PITCH_ROWS = 8
export const SYNTH_DEFAULT_PITCH_MIN = 48
export const SYNTH_DEFAULT_PITCH_MAX = 72
export const SYNTH_PITCH_ROW_HEIGHT = 32

export function getSynthPitchRange(track, selectedPitch = null) {
  const notes = (track?.sequence ?? []).map(event => event.note).filter(note => Number.isInteger(note))
  if (selectedPitch != null && Number.isInteger(selectedPitch)) notes.push(selectedPitch)
  if (notes.length === 0) return { minMidi: SYNTH_DEFAULT_PITCH_MIN, maxMidi: SYNTH_DEFAULT_PITCH_MAX }
  const min = Math.max(0, Math.min(...notes) - 12)
  const max = Math.min(127, Math.max(...notes) + 12)
  return { minMidi: min, maxMidi: Math.max(max, selectedPitch ?? max) }
}

export function synthPitchRows(minMidi, maxMidi) {
  const rows = []
  let index = 0
  for (let midi = maxMidi; midi >= minMidi; midi--, index++) {
    rows.push({
      index,
      midi,
      noteName: midiToNoteName(midi),
      isWhite: isWhiteKey(midi),
      octaveLabel: midi % 12 === 0,
    })
  }
  return rows
}

export function isWhiteKey(midi) {
  return [0, 2, 4, 5, 7, 9, 11].includes(Number(midi) % 12)
}

export function getGridStepDenominator(grid) {
  const match = /^1\/(\d+)$/.exec(String(grid ?? ''))
  const denominator = match ? Number(match[1]) : 16
  return [1, 2, 4, 8, 16, 32].includes(denominator) ? denominator : 16
}

function normalizeAdsr(input = {}) {
  return {
    attack: clampNumber(input?.attack, 0, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_TRACK_ADSR.attack),
    decay: clampNumber(input?.decay, 0, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_TRACK_ADSR.decay),
    sustain: clampNumber(input?.sustain, 0, 1, SYNTH_DEFAULT_TRACK_ADSR.sustain),
    release: clampNumber(input?.release, 0, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_TRACK_ADSR.release),
  }
}

function normalizeVibrato(input = {}) {
  return {
    enabled: input?.enabled === true,
    rateHz: clampNumber(input?.rateHz, 0, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_TRACK_VIBRATO.rateHz),
    depthHz: clampNumber(input?.depthHz, 0, Number.POSITIVE_INFINITY, SYNTH_DEFAULT_TRACK_VIBRATO.depthHz),
  }
}

function clampNumber(value, min, max, fallback) {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function clampInteger(value, min, max, fallback) {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  const int = Math.trunc(n)
  if (int < min || int > max) return fallback
  return int
}
